/**
 * Shadows and elevation system for Turn Connect applications.
 * Consistent depth and layering across components.
 */

export const shadows = {
  // Basic shadows
  none: 'none',

  // Subtle shadows for cards and elements
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  md: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  lg: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  xl: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  '2xl': '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  '3xl': '0 35px 60px -12px rgba(0, 0, 0, 0.25)',

  // Inner shadows
  inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
  innerLg: 'inset 0 4px 8px 0 rgba(0, 0, 0, 0.1)',

  // Component-specific shadows (Based on Style Guide)
  button: {
    none: 'none',
    subtle: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    default: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    hover: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    active: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
    focus: '0 0 0 3px rgba(218, 86, 44, 0.25)', // Orange focus ring (#DA562C)
  },

  card: {
    default: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    hover: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    elevated: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  },

  modal: {
    backdrop: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    dialog: '0 35px 60px -12px rgba(0, 0, 0, 0.25)',
  },

  dropdown: {
    default: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    large: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },

  toast: {
    default: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    elevated: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },

  input: {
    default: 'inset 0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    focus: '0 0 0 3px rgba(218, 86, 44, 0.25)', // Orange focus ring (#DA562C)
    error: '0 0 0 3px rgba(255, 107, 107, 0.25)', // Red focus ring
    success: '0 0 0 3px rgba(76, 175, 80, 0.25)', // Green focus ring
  },
};

// Elevation levels (Material Design inspired)
export const elevation = {
  // Surface levels
  0: shadows.none,
  1: shadows.sm,
  2: shadows.md,
  3: shadows.lg,
  4: shadows.xl,
  5: shadows['2xl'],
  6: shadows['3xl'],

  // Semantic elevation
  surface: shadows.none,
  raised: shadows.sm,
  card: shadows.md,
  popup: shadows.lg,
  modal: shadows['2xl'],
  tooltip: shadows.lg,
  overlay: shadows['3xl'],
};

// Border styles
export const borders = {
  // Border widths
  width: {
    none: 0,
    thin: 1,
    medium: 2,
    thick: 4,
  },

  // Border styles
  style: {
    solid: 'solid',
    dashed: 'dashed',
    dotted: 'dotted',
    none: 'none',
  },

  // Border radius
  radius: {
    none: 0,
    xs: 2,
    sm: 4,
    md: 6,
    lg: 8,
    xl: 12,
    '2xl': 16,
    '3xl': 24,
    full: 9999,
  },

  // Component-specific borders
  button: {
    radius: 6,
    width: 1,
  },

  input: {
    radius: 6,
    width: 1,
  },

  card: {
    radius: 8,
    width: 1,
  },

  modal: {
    radius: 12,
    width: 0,
  },

  avatar: {
    radius: 9999, // Full circle
    width: 0,
  },

  badge: {
    radius: 4,
    width: 0,
  },
};

// Focus ring styles (Based on Style Guide - Orange primary)
export const focusRing = {
  default: {
    outline: 'none',
    boxShadow: '0 0 0 3px rgba(218, 86, 44, 0.25)', // Orange (#DA562C)
  },

  error: {
    outline: 'none',
    boxShadow: '0 0 0 3px rgba(255, 107, 107, 0.25)',
  },

  success: {
    outline: 'none',
    boxShadow: '0 0 0 3px rgba(76, 175, 80, 0.25)',
  },

  warning: {
    outline: 'none',
    boxShadow: '0 0 0 3px rgba(255, 193, 7, 0.25)',
  },
};

export type Shadows = typeof shadows;
export type Elevation = typeof elevation;
export type Borders = typeof borders;
export type FocusRing = typeof focusRing;
